import React, { useEffect, useState, useMemo, useRef } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
    Plus, X, Trash2, Cloud, Camera, Image as ImageIcon, MessageSquareQuote,
    Package, Layers, DollarSign, Users as UsersIcon, TrendingUp, CheckCircle2,
} from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis } from "recharts";

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "") + "/api";
const CURRENCY = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

/* ============================================================
   FIELD TAB (Daily Logs + Photos + Weather)
   ============================================================ */
export function FieldTab({ projectId, project }) {
    const [sub, setSub] = useState("today");
    return (
        <div className="space-y-4" data-testid="tab-field-content">
            <div className="flex items-center gap-2">
                {[["today", "Today's Log"], ["history", "Field History"], ["photos", "Photos"]].map(([k, label]) => (
                    <button key={k} onClick={() => setSub(k)} className={`text-xs px-3 py-1.5 border ${sub === k ? "border-navy bg-secondary" : "border-border"}`} data-testid={`field-sub-${k}`}>{label}</button>
                ))}
            </div>
            {sub === "today" && <DailyLogComposer projectId={projectId} project={project} />}
            {sub === "history" && <FieldHistory projectId={projectId} />}
            {sub === "photos" && <PhotosPanel projectId={projectId} />}
        </div>
    );
}

function DailyLogComposer({ projectId, project }) {
    const today = new Date().toISOString().slice(0, 10);
    const [form, setForm] = useState({
        log_date: today, workers_count: 0, subcontractors_on_site: [], work_performed: [],
        materials_delivered: [], equipment_on_site: [], visitors: [], safety_issues: [],
        delays: [], problems: [], decisions: [], tomorrow_plan: "",
        weather: null, voice_notes: [],
    });
    const [weatherLoading, setWeatherLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    // Try to auto-fill weather from project property or manual lat/lon
    const fetchWeather = async () => {
        setWeatherLoading(true);
        try {
            // Try to get property lat/lon; fallback to Miami
            let lat = 25.7617, lon = -80.1918;
            if (project?.property_id) {
                try {
                    const { data: prop } = await api.get(`/properties/${project.property_id}`);
                    if (prop?.latitude && prop?.longitude) { lat = prop.latitude; lon = prop.longitude; }
                } catch { /* fall through */ }
            }
            const { data } = await api.get(`/weather?lat=${lat}&lon=${lon}&date=${form.log_date}`);
            setForm((f) => ({ ...f, weather: data }));
            toast.success(`Weather: ${data.summary} · ${data.temp_high}°/${data.temp_low}°`);
        } catch { toast.error("Weather unavailable"); }
        finally { setWeatherLoading(false); }
    };

    const save = async () => {
        setSaving(true);
        try {
            await api.post(`/projects/${projectId}/daily-logs`, form);
            toast.success("Daily log submitted");
            setForm({ ...form, work_performed: [], visitors: [], safety_issues: [], delays: [], problems: [], decisions: [], voice_notes: [] });
        } catch { toast.error("Save failed"); }
        finally { setSaving(false); }
    };

    return (
        <div className="grid md:grid-cols-3 gap-4" data-testid="daily-log-composer">
            <div className="md:col-span-2 space-y-3">
                <div className="flex flex-wrap items-center gap-3 border border-border p-3">
                    <label className="overline">Date</label>
                    <input type="date" value={form.log_date} onChange={(e) => setForm({ ...form, log_date: e.target.value })} className="border border-border bg-background px-3 py-1.5 text-sm" data-testid="dlog-date" />
                    <label className="overline ml-4">Workers</label>
                    <input type="number" value={form.workers_count} onChange={(e) => setForm({ ...form, workers_count: parseInt(e.target.value) || 0 })} className="border border-border bg-background px-3 py-1.5 text-sm w-20" data-testid="dlog-workers" />
                    <button onClick={fetchWeather} disabled={weatherLoading} className="ml-auto inline-flex items-center gap-2 border border-border px-3 py-1.5 text-xs" data-testid="dlog-weather-btn">
                        <Cloud size={12} /> {weatherLoading ? "Loading…" : "Auto-fill weather"}
                    </button>
                </div>
                {form.weather?.available && (
                    <div className="border border-border p-3 text-xs" data-testid="dlog-weather-display">
                        <b>{form.weather.summary}</b> · High {form.weather.temp_high}°F · Low {form.weather.temp_low}°F · Precip {form.weather.precipitation_in}" · Wind {form.weather.wind_mph} mph
                    </div>
                )}
                <ListEditor label="Work performed" items={form.work_performed} onChange={(v) => setForm({ ...form, work_performed: v })} testid="dlog-work" />
                <ListEditor label="Subcontractors on site" items={form.subcontractors_on_site} onChange={(v) => setForm({ ...form, subcontractors_on_site: v })} testid="dlog-subs" />
                <ListEditor label="Materials delivered" items={form.materials_delivered} onChange={(v) => setForm({ ...form, materials_delivered: v })} testid="dlog-materials" />
                <ListEditor label="Equipment on site" items={form.equipment_on_site} onChange={(v) => setForm({ ...form, equipment_on_site: v })} testid="dlog-equipment" />
            </div>
            <div className="space-y-3">
                <ListEditor label="Safety issues" items={form.safety_issues} onChange={(v) => setForm({ ...form, safety_issues: v })} testid="dlog-safety" />
                <ListEditor label="Delays" items={form.delays} onChange={(v) => setForm({ ...form, delays: v })} testid="dlog-delays" />
                <ListEditor label="Decisions" items={form.decisions} onChange={(v) => setForm({ ...form, decisions: v })} testid="dlog-decisions" />
                <div>
                    <label className="overline block mb-1">Tomorrow's plan</label>
                    <textarea rows={3} value={form.tomorrow_plan} onChange={(e) => setForm({ ...form, tomorrow_plan: e.target.value })} className="input" data-testid="dlog-tomorrow" />
                </div>
                <VoiceNoteInput onAdd={(text) => setForm({ ...form, voice_notes: [...form.voice_notes, { text, at: new Date().toISOString() }] })} notes={form.voice_notes} />
                <button onClick={save} disabled={saving} className="w-full bg-navy text-arch-white py-2 text-sm hover:opacity-90 disabled:opacity-50" data-testid="dlog-save">
                    {saving ? "Saving…" : "Submit daily log"}
                </button>
            </div>
        </div>
    );
}

function ListEditor({ label, items, onChange, testid }) {
    const [val, setVal] = useState("");
    const add = () => { if (!val.trim()) return; onChange([...items, val.trim()]); setVal(""); };
    return (
        <div>
            <label className="overline block mb-1">{label} <span className="text-muted-foreground">({items.length})</span></label>
            <div className="flex gap-1">
                <input value={val} onChange={(e) => setVal(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())} placeholder="Add…" className="input" data-testid={`${testid}-input`} />
                <button type="button" onClick={add} className="bg-navy text-arch-white px-3 text-sm" data-testid={`${testid}-add`}><Plus size={12} /></button>
            </div>
            {items.length > 0 && (
                <ul className="mt-1 space-y-0.5">
                    {items.map((it, i) => (
                        <li key={i} className="flex items-center gap-2 text-xs bg-secondary/40 px-2 py-1">
                            <span className="flex-1">{it}</span>
                            <button onClick={() => onChange(items.filter((_, x) => x !== i))} className="text-muted-foreground hover:text-destructive" data-testid={`${testid}-remove-${i}`}><X size={10} /></button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

function VoiceNoteInput({ onAdd, notes }) {
    const [text, setText] = useState("");
    return (
        <div>
            <label className="overline block mb-1">Voice notes ({notes.length}) <span className="text-muted-foreground italic normal-case">Text only — audio + transcription coming</span></label>
            <textarea rows={2} value={text} onChange={(e) => setText(e.target.value)} placeholder="Type or dictate…" className="input" data-testid="dlog-voice-input" />
            <button onClick={() => { if (text.trim()) { onAdd(text.trim()); setText(""); } }} className="mt-1 text-xs border border-border px-3 py-1.5" data-testid="dlog-voice-add">Add voice note</button>
        </div>
    );
}

function FieldHistory({ projectId }) {
    const [rows, setRows] = useState([]);
    useEffect(() => {
        (async () => {
            const { data } = await api.get(`/projects/${projectId}/daily-logs`);
            setRows(data.items || []);
        })();
    }, [projectId]);
    return (
        <div className="border border-border" data-testid="field-history">
            {rows.length === 0 && <div className="p-6 text-sm text-muted-foreground">No daily logs yet.</div>}
            {rows.map((r) => (
                <div key={r.id} className="border-b border-border last:border-b-0 p-4" data-testid={`dlog-row-${r.id}`}>
                    <div className="flex items-center justify-between">
                        <div className="font-medium">{r.log_date}</div>
                        <div className="text-xs text-muted-foreground">
                            {r.weather?.summary && `${r.weather.summary} · ${r.weather.temp_high}°/${r.weather.temp_low}°`} · {r.workers_count} workers
                        </div>
                    </div>
                    {r.work_performed?.length > 0 && <div className="mt-2 text-sm"><b>Work:</b> {r.work_performed.join("; ")}</div>}
                    {r.delays?.length > 0 && <div className="mt-1 text-xs text-amber-500"><b>Delays:</b> {r.delays.join("; ")}</div>}
                    {r.safety_issues?.length > 0 && <div className="mt-1 text-xs text-destructive"><b>Safety:</b> {r.safety_issues.join("; ")}</div>}
                    {r.tomorrow_plan && <div className="mt-1 text-xs text-muted-foreground italic">Tomorrow: {r.tomorrow_plan}</div>}
                </div>
            ))}
        </div>
    );
}

function PhotosPanel({ projectId }) {
    const [photos, setPhotos] = useState([]);
    const [tagFilter, setTagFilter] = useState("");
    const [selectedPhoto, setSelectedPhoto] = useState(null);
    const [uploading, setUploading] = useState(false);
    const fileRef = useRef(null);

    const load = async () => {
        const q = tagFilter ? `?tag_room=${encodeURIComponent(tagFilter)}` : "";
        const { data } = await api.get(`/projects/${projectId}/photos${q}`);
        setPhotos(data.items || []);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId, tagFilter]);

    const upload = async (files) => {
        if (!files.length) return;
        setUploading(true);
        for (const f of files) {
            const fd = new FormData();
            fd.append("file", f);
            fd.append("caption", "");
            try { await api.post(`/projects/${projectId}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } }); }
            catch { toast.error(`Upload failed: ${f.name}`); }
        }
        setUploading(false);
        load();
        toast.success(`${files.length} photo(s) uploaded`);
    };

    const beforeAfter = photos.filter((p) => p.is_before || p.is_after);

    return (
        <div className="space-y-4" data-testid="photos-panel">
            <div className="flex items-center gap-3">
                <input placeholder="Filter by room tag…" value={tagFilter} onChange={(e) => setTagFilter(e.target.value)} className="input max-w-xs" data-testid="photos-tag-filter" />
                <input ref={fileRef} type="file" multiple accept="image/*" className="hidden" onChange={(e) => upload(Array.from(e.target.files))} data-testid="photos-upload-input" />
                <button onClick={() => fileRef.current?.click()} disabled={uploading} className="ml-auto inline-flex items-center gap-2 bg-navy text-arch-white px-3 py-1.5 text-xs disabled:opacity-50" data-testid="photos-upload-btn">
                    <Camera size={12} /> {uploading ? "Uploading…" : "Upload photos"}
                </button>
            </div>

            {photos.length === 0 ? (
                <div className="border border-border p-10 text-center text-sm text-muted-foreground" data-testid="photos-empty">
                    <ImageIcon size={28} className="mx-auto mb-3" />
                    No photos yet.
                </div>
            ) : (
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2" data-testid="photos-grid">
                    {photos.map((p) => (
                        <button key={p.id} onClick={() => setSelectedPhoto(p)} className="group relative border border-border overflow-hidden hover:opacity-90" data-testid={`photo-${p.id}`}>
                            <img src={`${API_BASE}${p.thumbnail_signed.replace('/api', '')}`} alt={p.caption} className="w-full h-32 object-cover" />
                            {p.is_before && <span className="absolute top-1 left-1 chip chip-navy text-[9px] bg-background">BEFORE</span>}
                            {p.is_after && <span className="absolute top-1 left-1 chip chip-ai text-[9px] bg-background">AFTER</span>}
                        </button>
                    ))}
                </div>
            )}

            {beforeAfter.length > 0 && (
                <div>
                    <div className="overline mb-2">Before / After pairs ({beforeAfter.filter((p) => p.is_before).length})</div>
                    <div className="grid md:grid-cols-2 gap-3">
                        {beforeAfter.filter((p) => p.is_before && p.related_photo_id).map((p) => {
                            const after = photos.find((x) => x.id === p.related_photo_id);
                            if (!after) return null;
                            return (
                                <div key={p.id} className="grid grid-cols-2 gap-1 border border-border" data-testid={`ba-pair-${p.id}`}>
                                    <div><img src={`${API_BASE}${p.thumbnail_signed.replace('/api', '')}`} alt="before" className="w-full h-32 object-cover" /><div className="text-[10px] p-1 text-center bg-secondary/40">Before</div></div>
                                    <div><img src={`${API_BASE}${after.thumbnail_signed.replace('/api', '')}`} alt="after" className="w-full h-32 object-cover" /><div className="text-[10px] p-1 text-center bg-secondary/40">After</div></div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {selectedPhoto && <PhotoLightbox photo={selectedPhoto} allPhotos={photos} onClose={() => setSelectedPhoto(null)} onChanged={load} />}
        </div>
    );
}

function PhotoLightbox({ photo, allPhotos, onClose, onChanged }) {
    const [tags, setTags] = useState(photo.tags || {});
    const [caption, setCaption] = useState(photo.caption || "");
    const [pairId, setPairId] = useState("");

    const save = async () => {
        try {
            await api.patch(`/photos/${photo.id}`, { caption, tags });
            toast.success("Saved"); onChanged?.();
        } catch { toast.error("Save failed"); }
    };
    const markBefore = async () => {
        await api.patch(`/photos/${photo.id}`, { is_before: true, is_after: false });
        toast.success("Marked BEFORE"); onChanged?.();
    };
    const pair = async () => {
        if (!pairId) return;
        await api.post(`/photos/pair-before-after`, { before_photo_id: photo.id, after_photo_id: pairId });
        toast.success("Paired"); onChanged?.(); onClose();
    };
    const del = async () => {
        if (!window.confirm("Delete photo?")) return;
        await api.delete(`/photos/${photo.id}`);
        onChanged?.(); onClose();
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose} data-testid="photo-lightbox">
            <div className="bg-background border border-border max-w-5xl w-full grid md:grid-cols-3" onClick={(e) => e.stopPropagation()}>
                <div className="md:col-span-2 bg-black flex items-center justify-center">
                    <img src={`${API_BASE}${photo.url.replace('/api', '')}`} alt={caption} className="max-h-[70vh] w-full object-contain" />
                </div>
                <div className="p-4 space-y-3 text-xs">
                    <div className="flex items-center justify-between">
                        <div className="overline">Photo</div>
                        <button onClick={onClose}><X size={14} /></button>
                    </div>
                    <div>
                        <label className="overline block mb-1">Caption</label>
                        <input value={caption} onChange={(e) => setCaption(e.target.value)} className="input" data-testid="photo-caption" />
                    </div>
                    <div>
                        <label className="overline block mb-1">Room / area tag</label>
                        <input value={tags.room || ""} onChange={(e) => setTags({ ...tags, room: e.target.value })} className="input" placeholder="Kitchen, Master bath…" data-testid="photo-tag-room" />
                    </div>
                    <div>
                        <label className="overline block mb-1">Trade tag</label>
                        <input value={tags.trade || ""} onChange={(e) => setTags({ ...tags, trade: e.target.value })} className="input" placeholder="Framing, Electrical…" data-testid="photo-tag-trade" />
                    </div>
                    <button onClick={save} className="w-full bg-navy text-arch-white py-1.5" data-testid="photo-save">Save tags</button>
                    <hr className="border-border" />
                    <div>
                        <div className="overline mb-1">Mark as BEFORE / pair with AFTER</div>
                        <button onClick={markBefore} className="w-full border border-border py-1.5 mb-2 text-[11px]" data-testid="photo-mark-before">Mark as BEFORE</button>
                        <select value={pairId} onChange={(e) => setPairId(e.target.value)} className="input" data-testid="photo-pair-select">
                            <option value="">— Pair with AFTER photo —</option>
                            {allPhotos.filter((p) => p.id !== photo.id).map((p) => <option key={p.id} value={p.id}>{p.filename}</option>)}
                        </select>
                        <button onClick={pair} disabled={!pairId} className="w-full bg-navy text-arch-white py-1.5 mt-2 disabled:opacity-50" data-testid="photo-pair-save">Pair</button>
                    </div>
                    <hr className="border-border" />
                    <button onClick={del} className="w-full text-destructive text-xs inline-flex items-center justify-center gap-1" data-testid="photo-delete"><Trash2 size={11} /> Delete</button>
                </div>
            </div>
        </div>
    );
}

/* ============================================================
   COORDINATION TAB (RFIs + Submittals + Change Orders)
   ============================================================ */
export function CoordinationTab({ projectId }) {
    const [sub, setSub] = useState("rfis");
    return (
        <div className="space-y-4" data-testid="tab-coordination-content">
            <div className="flex items-center gap-2">
                {[["rfis", "RFIs"], ["submittals", "Submittals"], ["changes", "Change Orders"]].map(([k, l]) => (
                    <button key={k} onClick={() => setSub(k)} className={`text-xs px-3 py-1.5 border ${sub === k ? "border-navy bg-secondary" : "border-border"}`} data-testid={`coord-sub-${k}`}>{l}</button>
                ))}
            </div>
            {sub === "rfis" && <RFIsPanel projectId={projectId} />}
            {sub === "submittals" && <SubmittalsPanel projectId={projectId} />}
            {sub === "changes" && <ChangeOrdersPanel projectId={projectId} />}
        </div>
    );
}

function RFIsPanel({ projectId }) {
    const [rows, setRows] = useState([]);
    const [creating, setCreating] = useState(false);
    const [question, setQuestion] = useState("");
    const [selected, setSelected] = useState(null);

    const load = async () => {
        const { data } = await api.get(`/projects/${projectId}/rfis`);
        setRows(data.items || []);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);

    const create = async (e) => {
        e.preventDefault();
        if (!question.trim()) return;
        await api.post(`/projects/${projectId}/rfis`, { question });
        setQuestion(""); setCreating(false); load(); toast.success("RFI created");
    };

    return (
        <div>
            <div className="flex items-center justify-between mb-3">
                <div className="text-xs text-muted-foreground">{rows.length} RFIs</div>
                <button onClick={() => setCreating(true)} className="bg-navy text-arch-white text-xs px-3 py-1.5 inline-flex items-center gap-2" data-testid="rfi-new-btn"><Plus size={12} /> New RFI</button>
            </div>
            {creating && (
                <form onSubmit={create} className="border border-border p-3 mb-3 flex gap-2" data-testid="rfi-form">
                    <textarea rows={2} placeholder="Question…" value={question} onChange={(e) => setQuestion(e.target.value)} className="input" data-testid="rfi-question" />
                    <button type="submit" className="bg-navy text-arch-white px-3 text-xs" data-testid="rfi-save">Save</button>
                    <button type="button" onClick={() => setCreating(false)} className="border border-border px-3 text-xs">Cancel</button>
                </form>
            )}
            <div className="border border-border" data-testid="rfis-table">
                <div className="grid px-3 py-2 border-b border-border text-[10px] uppercase tracking-widest text-muted-foreground" style={{ gridTemplateColumns: "60px 3fr 1fr 1fr" }}>
                    <div>#</div><div>Question</div><div>Status</div><div>Created</div>
                </div>
                {rows.map((r) => (
                    <button key={r.id} onClick={() => setSelected(r)} className="w-full grid px-3 py-2 border-b border-border last:border-b-0 text-xs text-left hover:bg-secondary/40" style={{ gridTemplateColumns: "60px 3fr 1fr 1fr" }} data-testid={`rfi-row-${r.id}`}>
                        <div className="font-mono">#{r.rfi_number}</div>
                        <div className="truncate">{r.question}</div>
                        <div className="capitalize">{r.status}</div>
                        <div className="text-muted-foreground">{r.created_at?.slice(0, 10)}</div>
                    </button>
                ))}
                {rows.length === 0 && <div className="p-4 text-sm text-muted-foreground">No RFIs.</div>}
            </div>
            {selected && <RFIDetail rfi={selected} onClose={() => setSelected(null)} onSaved={() => { setSelected(null); load(); }} />}
        </div>
    );
}

function RFIDetail({ rfi, onClose, onSaved }) {
    const [response, setResponse] = useState(rfi.response || "");
    const [status, setStatus] = useState(rfi.status);
    const save = async () => {
        await api.patch(`/rfis/${rfi.id}`, { response, status });
        toast.success("Saved"); onSaved?.();
    };
    return (
        <Modal onClose={onClose} title={`RFI #${rfi.rfi_number}`} testid="rfi-detail">
            <div className="p-5 space-y-3">
                <div><div className="overline">Question</div><div className="text-sm mt-1">{rfi.question}</div></div>
                <div>
                    <label className="overline block mb-1">Response</label>
                    <textarea rows={4} value={response} onChange={(e) => setResponse(e.target.value)} className="input" data-testid="rfi-response" />
                </div>
                <div>
                    <label className="overline block mb-1">Status</label>
                    <select value={status} onChange={(e) => setStatus(e.target.value)} className="input" data-testid="rfi-status">
                        {["open", "answered", "closed"].map((s) => <option key={s}>{s}</option>)}
                    </select>
                </div>
                <button onClick={save} className="bg-navy text-arch-white px-4 py-1.5 text-sm" data-testid="rfi-save-btn">Save</button>
            </div>
        </Modal>
    );
}

function SubmittalsPanel({ projectId }) {
    const [rows, setRows] = useState([]);
    const [creating, setCreating] = useState(false);
    const [form, setForm] = useState({ spec_section: "", product_name: "" });

    const load = async () => {
        const { data } = await api.get(`/projects/${projectId}/submittals`);
        setRows(data.items || []);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);

    const create = async (e) => {
        e.preventDefault();
        await api.post(`/projects/${projectId}/submittals`, form);
        setForm({ spec_section: "", product_name: "" }); setCreating(false); load();
        toast.success("Submittal created");
    };
    const updateStatus = async (r, review_status) => {
        await api.patch(`/submittals/${r.id}`, { review_status });
        load();
    };
    return (
        <div>
            <div className="flex items-center justify-between mb-3">
                <div className="text-xs text-muted-foreground">{rows.length} submittals</div>
                <button onClick={() => setCreating(true)} className="bg-navy text-arch-white text-xs px-3 py-1.5 inline-flex items-center gap-2" data-testid="sub-new-btn"><Plus size={12} /> New Submittal</button>
            </div>
            {creating && (
                <form onSubmit={create} className="border border-border p-3 mb-3 grid grid-cols-3 gap-2" data-testid="sub-form">
                    <input placeholder="Spec section (e.g. 09 30 00)" value={form.spec_section} onChange={(e) => setForm({ ...form, spec_section: e.target.value })} className="input" data-testid="sub-spec" required />
                    <input placeholder="Product name" value={form.product_name} onChange={(e) => setForm({ ...form, product_name: e.target.value })} className="input" data-testid="sub-product" required />
                    <div className="flex gap-1"><button type="submit" className="bg-navy text-arch-white px-3 flex-1 text-xs" data-testid="sub-save">Save</button><button type="button" onClick={() => setCreating(false)} className="border border-border px-3 text-xs">×</button></div>
                </form>
            )}
            <div className="border border-border" data-testid="submittals-table">
                <div className="grid px-3 py-2 border-b border-border text-[10px] uppercase tracking-widest text-muted-foreground" style={{ gridTemplateColumns: "60px 1fr 2fr 1fr 1fr" }}>
                    <div>#</div><div>Spec §</div><div>Product</div><div>Status</div><div>Submitted</div>
                </div>
                {rows.map((r) => (
                    <div key={r.id} className="grid px-3 py-2 border-b border-border last:border-b-0 text-xs items-center" style={{ gridTemplateColumns: "60px 1fr 2fr 1fr 1fr" }} data-testid={`sub-row-${r.id}`}>
                        <div className="font-mono">#{r.submittal_number}</div>
                        <div>{r.spec_section}</div>
                        <div>{r.product_name}</div>
                        <select value={r.review_status} onChange={(e) => updateStatus(r, e.target.value)} className="input text-xs" data-testid={`sub-status-${r.id}`}>
                            {["pending", "approved_as_noted", "approved", "revise_and_resubmit", "rejected"].map((s) => <option key={s}>{s}</option>)}
                        </select>
                        <div className="text-muted-foreground">{r.submitted_date?.slice(0, 10)}</div>
                    </div>
                ))}
                {rows.length === 0 && <div className="p-4 text-sm text-muted-foreground">No submittals.</div>}
            </div>
        </div>
    );
}

function ChangeOrdersPanel({ projectId }) {
    const [rows, setRows] = useState([]);
    const [creating, setCreating] = useState(false);
    const [form, setForm] = useState({
        description: "", reason: "owner_change", scope_narrative: "",
        labor_cost: 0, materials_cost: 0, subcontractor_cost: 0, equipment_cost: 0,
        markup_pct: 10, schedule_impact_days: 0, target_category: "Hard Costs",
    });

    const load = async () => {
        const { data } = await api.get(`/projects/${projectId}/change-orders`);
        setRows(data.items || []);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);

    const create = async (e) => {
        e.preventDefault();
        try {
            await api.post(`/projects/${projectId}/change-orders`, form);
            setCreating(false); load(); toast.success("Change order created");
            setForm({ ...form, description: "", scope_narrative: "" });
        } catch { toast.error("Failed"); }
    };
    const approve = async (co) => {
        if (!window.confirm(`Approve CO #${co.co_number} for ${CURRENCY.format(co.total_cost)}?\nThis will increase the budget.`)) return;
        try {
            await api.post(`/change-orders/${co.id}/approve`);
            load(); toast.success(`CO #${co.co_number} approved, budget updated`);
        } catch (e) { toast.error(e?.response?.data?.detail || "Approve failed"); }
    };

    const total = form.labor_cost + form.materials_cost + form.subcontractor_cost + form.equipment_cost;
    const withMarkup = total * (1 + form.markup_pct / 100);

    return (
        <div>
            <div className="flex items-center justify-between mb-3">
                <div className="text-xs text-muted-foreground">{rows.length} change orders</div>
                <button onClick={() => setCreating(true)} className="bg-navy text-arch-white text-xs px-3 py-1.5 inline-flex items-center gap-2" data-testid="co-new-btn"><Plus size={12} /> New CO</button>
            </div>
            {creating && (
                <form onSubmit={create} className="border border-border p-4 mb-3 space-y-3" data-testid="co-form">
                    <div className="grid md:grid-cols-2 gap-3">
                        <div><label className="overline block mb-1">Description</label><input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="input" required data-testid="co-description" /></div>
                        <div><label className="overline block mb-1">Reason</label><select value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="input" data-testid="co-reason">{["owner_change", "site_condition", "scope_gap", "regulatory", "other"].map((r) => <option key={r}>{r}</option>)}</select></div>
                    </div>
                    <div><label className="overline block mb-1">Scope narrative</label><textarea rows={2} value={form.scope_narrative} onChange={(e) => setForm({ ...form, scope_narrative: e.target.value })} className="input" data-testid="co-scope" /></div>
                    <div className="grid grid-cols-4 gap-2">
                        {["labor_cost", "materials_cost", "subcontractor_cost", "equipment_cost"].map((k) => (
                            <div key={k}>
                                <label className="overline block mb-1">{k.split("_")[0]}</label>
                                <input type="number" value={form[k]} onChange={(e) => setForm({ ...form, [k]: parseFloat(e.target.value) || 0 })} className="input" data-testid={`co-${k}`} />
                            </div>
                        ))}
                    </div>
                    <div className="grid grid-cols-3 gap-2 items-end">
                        <div><label className="overline block mb-1">Markup %</label><input type="number" value={form.markup_pct} onChange={(e) => setForm({ ...form, markup_pct: parseFloat(e.target.value) || 0 })} className="input" data-testid="co-markup" /></div>
                        <div><label className="overline block mb-1">Schedule impact days</label><input type="number" value={form.schedule_impact_days} onChange={(e) => setForm({ ...form, schedule_impact_days: parseInt(e.target.value) || 0 })} className="input" data-testid="co-days" /></div>
                        <div><label className="overline block mb-1">Target category</label><input value={form.target_category} onChange={(e) => setForm({ ...form, target_category: e.target.value })} className="input" data-testid="co-category" /></div>
                    </div>
                    <div className="flex items-center justify-between p-2 bg-secondary/40 text-sm">
                        <span>Total with markup:</span>
                        <span className="font-display text-xl italic" data-testid="co-computed-total">{CURRENCY.format(withMarkup)}</span>
                    </div>
                    <div className="flex justify-end gap-2"><button type="button" onClick={() => setCreating(false)} className="border border-border px-4 py-1.5 text-sm">Cancel</button><button type="submit" className="bg-navy text-arch-white px-4 py-1.5 text-sm" data-testid="co-save">Save draft</button></div>
                </form>
            )}
            <div className="border border-border" data-testid="cos-table">
                <div className="grid px-3 py-2 border-b border-border text-[10px] uppercase tracking-widest text-muted-foreground" style={{ gridTemplateColumns: "60px 2fr 1fr 1fr 1fr 100px" }}>
                    <div>#</div><div>Description</div><div>Reason</div><div>Total</div><div>Status</div><div></div>
                </div>
                {rows.map((r) => (
                    <div key={r.id} className="grid px-3 py-2 border-b border-border last:border-b-0 text-xs items-center" style={{ gridTemplateColumns: "60px 2fr 1fr 1fr 1fr 100px" }} data-testid={`co-row-${r.id}`}>
                        <div className="font-mono">#{r.co_number}</div>
                        <div>{r.description}</div>
                        <div className="text-muted-foreground">{r.reason}</div>
                        <div>{CURRENCY.format(r.total_cost)}</div>
                        <div className={r.approval_status === "approved" ? "text-emerald-500" : "text-muted-foreground"}>{r.approval_status}</div>
                        <div className="flex justify-end">
                            {r.approval_status !== "approved" && <button onClick={() => approve(r)} className="text-[11px] bg-navy text-arch-white px-2 py-1" data-testid={`co-approve-${r.id}`}>Approve</button>}
                        </div>
                    </div>
                ))}
                {rows.length === 0 && <div className="p-4 text-sm text-muted-foreground">No change orders.</div>}
            </div>
        </div>
    );
}

/* ============================================================
   CAPITAL TAB (cap stack + contributions + distributions)
   ============================================================ */
export function CapitalTab({ projectId }) {
    const [summary, setSummary] = useState(null);
    const [contributions, setContributions] = useState([]);
    const [distributions, setDistributions] = useState([]);
    const [investors, setInvestors] = useState([]);
    const [lenders, setLenders] = useState([]);
    const [addingContrib, setAddingContrib] = useState(false);
    const [addingDist, setAddingDist] = useState(false);

    const load = async () => {
        const [{ data: s }, { data: c }, { data: d }, { data: I }, { data: L }] = await Promise.all([
            api.get(`/projects/${projectId}/capital-summary`),
            api.get(`/projects/${projectId}/capital-contributions`),
            api.get(`/projects/${projectId}/distributions`),
            api.get(`/investors`),
            api.get(`/lenders`),
        ]);
        setSummary(s); setContributions(c.items || []); setDistributions(d.items || []);
        setInvestors(I.items || []); setLenders(L.items || []);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);

    if (!summary) return <div className="p-6 text-sm text-muted-foreground">Loading capital summary…</div>;

    const stackColors = { equity: "#0F2747", preferred_equity: "#5B7FB0", mezzanine: "#DCEEFF", senior_debt: "#10b981", other: "#94a3b8" };

    return (
        <div className="space-y-6" data-testid="tab-capital-content">
            <div className="grid md:grid-cols-4 gap-3">
                <SumCard label="Project budget" value={CURRENCY.format(summary.project_total_budget)} />
                <SumCard label="Contributed" value={CURRENCY.format(summary.total_contributed)} />
                <SumCard label="Distributed" value={CURRENCY.format(summary.total_distributed)} />
                <SumCard label="Funded %" value={`${summary.funded_pct}%`} />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
                <div className="border border-border p-6" data-testid="cap-stack-viz">
                    <div className="overline mb-3">Cap stack</div>
                    {summary.cap_stack.length ? (
                        <ResponsiveContainer width="100%" height={220}>
                            <PieChart>
                                <Pie data={summary.cap_stack} dataKey="amount" nameKey="tranche" innerRadius={40} outerRadius={90}>
                                    {summary.cap_stack.map((s, i) => <Cell key={i} fill={stackColors[s.tranche] || "#94a3b8"} />)}
                                </Pie>
                                <Tooltip formatter={(v) => CURRENCY.format(v)} />
                            </PieChart>
                        </ResponsiveContainer>
                    ) : <div className="text-sm text-muted-foreground">No contributions recorded.</div>}
                    <ul className="mt-3 space-y-1 text-xs">
                        {summary.cap_stack.map((s) => (
                            <li key={s.tranche} className="flex items-center gap-2">
                                <span className="w-3 h-3 inline-block" style={{ background: stackColors[s.tranche] || "#94a3b8" }} />
                                <span className="capitalize">{s.tranche.replace(/_/g, " ")}</span>
                                <span className="ml-auto">{CURRENCY.format(s.amount)}</span>
                            </li>
                        ))}
                    </ul>
                </div>

                <div className="space-y-4">
                    <div className="border border-border p-4">
                        <div className="flex items-center justify-between mb-2">
                            <div className="overline">Contributions ({contributions.length})</div>
                            <button onClick={() => setAddingContrib(true)} className="bg-navy text-arch-white text-xs px-3 py-1" data-testid="add-contribution-btn"><Plus size={11} className="inline" /> Add</button>
                        </div>
                        <ul className="text-xs divide-y divide-border max-h-[180px] overflow-y-auto">
                            {contributions.map((c) => {
                                const from = investors.find((i) => i.id === c.investor_id)?.name || lenders.find((l) => l.id === c.lender_id)?.name || "—";
                                return (
                                    <li key={c.id} className="py-2 flex items-center gap-3" data-testid={`contrib-${c.id}`}>
                                        <span className="flex-1">{from}</span>
                                        <span className="text-muted-foreground capitalize">{c.contribution_type.replace(/_/g, " ")}</span>
                                        <span className="font-medium">{CURRENCY.format(c.amount)}</span>
                                    </li>
                                );
                            })}
                            {!contributions.length && <li className="py-3 text-muted-foreground">No contributions yet.</li>}
                        </ul>
                    </div>

                    <div className="border border-border p-4">
                        <div className="flex items-center justify-between mb-2">
                            <div className="overline">Distributions ({distributions.length})</div>
                            <button onClick={() => setAddingDist(true)} className="bg-navy text-arch-white text-xs px-3 py-1" data-testid="add-distribution-btn"><Plus size={11} className="inline" /> Add</button>
                        </div>
                        <ul className="text-xs divide-y divide-border max-h-[180px] overflow-y-auto">
                            {distributions.map((d) => {
                                const to = investors.find((i) => i.id === d.investor_id)?.name || lenders.find((l) => l.id === d.lender_id)?.name || "—";
                                return (
                                    <li key={d.id} className="py-2 flex items-center gap-3" data-testid={`dist-${d.id}`}>
                                        <span className="flex-1">{to}</span>
                                        <span className="text-muted-foreground capitalize">{d.distribution_type.replace(/_/g, " ")}</span>
                                        <span className="font-medium">{CURRENCY.format(d.amount)}</span>
                                    </li>
                                );
                            })}
                            {!distributions.length && <li className="py-3 text-muted-foreground">No distributions yet.</li>}
                        </ul>
                    </div>
                </div>
            </div>

            {addingContrib && <ContributionForm projectId={projectId} investors={investors} lenders={lenders} onClose={() => setAddingContrib(false)} onSaved={() => { setAddingContrib(false); load(); }} />}
            {addingDist && <DistributionForm projectId={projectId} investors={investors} lenders={lenders} onClose={() => setAddingDist(false)} onSaved={() => { setAddingDist(false); load(); }} />}
        </div>
    );
}

function SumCard({ label, value }) {
    return (
        <div className="border border-border p-4">
            <div className="overline">{label}</div>
            <div className="mt-1 font-display text-2xl italic">{value}</div>
        </div>
    );
}

function ContributionForm({ projectId, investors, lenders, onClose, onSaved }) {
    const [form, setForm] = useState({ investor_id: "", lender_id: "", contribution_type: "equity", amount: 0, expected_return_pct: 0 });
    const submit = async (e) => {
        e.preventDefault();
        if (!form.investor_id && !form.lender_id) { toast.error("Pick investor or lender"); return; }
        try {
            await api.post(`/projects/${projectId}/capital-contributions`, {
                ...form,
                investor_id: form.investor_id || undefined,
                lender_id: form.lender_id || undefined,
            });
            toast.success("Contribution added"); onSaved?.();
        } catch { toast.error("Failed"); }
    };
    return (
        <Modal onClose={onClose} title="Add capital contribution" testid="contribution-form">
            <form onSubmit={submit} className="p-5 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                    <div><label className="overline block mb-1">Investor</label><select value={form.investor_id} onChange={(e) => setForm({ ...form, investor_id: e.target.value, lender_id: "" })} className="input" data-testid="contrib-investor"><option value="">—</option>{investors.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}</select></div>
                    <div><label className="overline block mb-1">or Lender</label><select value={form.lender_id} onChange={(e) => setForm({ ...form, lender_id: e.target.value, investor_id: "" })} className="input" data-testid="contrib-lender"><option value="">—</option>{lenders.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}</select></div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                    <div><label className="overline block mb-1">Type</label><select value={form.contribution_type} onChange={(e) => setForm({ ...form, contribution_type: e.target.value })} className="input" data-testid="contrib-type">{["equity", "preferred_equity", "mezzanine", "senior_debt", "other"].map((t) => <option key={t}>{t}</option>)}</select></div>
                    <div><label className="overline block mb-1">Amount</label><input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: parseFloat(e.target.value) || 0 })} className="input" data-testid="contrib-amount" required /></div>
                    <div><label className="overline block mb-1">Expected return %</label><input type="number" value={form.expected_return_pct} onChange={(e) => setForm({ ...form, expected_return_pct: parseFloat(e.target.value) || 0 })} className="input" data-testid="contrib-return" /></div>
                </div>
                <div className="flex justify-end gap-2"><button type="button" onClick={onClose} className="border border-border px-4 py-1.5 text-sm">Cancel</button><button type="submit" className="bg-navy text-arch-white px-4 py-1.5 text-sm" data-testid="contrib-save">Save</button></div>
            </form>
        </Modal>
    );
}

function DistributionForm({ projectId, investors, lenders, onClose, onSaved }) {
    const [form, setForm] = useState({ investor_id: "", lender_id: "", distribution_type: "profit", amount: 0 });
    const submit = async (e) => {
        e.preventDefault();
        if (!form.investor_id && !form.lender_id) { toast.error("Pick investor or lender"); return; }
        try {
            await api.post(`/projects/${projectId}/distributions`, {
                ...form,
                investor_id: form.investor_id || undefined,
                lender_id: form.lender_id || undefined,
            });
            toast.success("Distribution added"); onSaved?.();
        } catch { toast.error("Failed"); }
    };
    return (
        <Modal onClose={onClose} title="Record distribution" testid="distribution-form">
            <form onSubmit={submit} className="p-5 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                    <div><label className="overline block mb-1">Investor</label><select value={form.investor_id} onChange={(e) => setForm({ ...form, investor_id: e.target.value, lender_id: "" })} className="input" data-testid="dist-investor"><option value="">—</option>{investors.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}</select></div>
                    <div><label className="overline block mb-1">or Lender</label><select value={form.lender_id} onChange={(e) => setForm({ ...form, lender_id: e.target.value, investor_id: "" })} className="input" data-testid="dist-lender"><option value="">—</option>{lenders.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}</select></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <div><label className="overline block mb-1">Type</label><select value={form.distribution_type} onChange={(e) => setForm({ ...form, distribution_type: e.target.value })} className="input" data-testid="dist-type">{["return_of_capital", "preferred_return", "profit", "interest_payment", "principal_payment", "other"].map((t) => <option key={t}>{t}</option>)}</select></div>
                    <div><label className="overline block mb-1">Amount</label><input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: parseFloat(e.target.value) || 0 })} className="input" data-testid="dist-amount" required /></div>
                </div>
                <div className="flex justify-end gap-2"><button type="button" onClick={onClose} className="border border-border px-4 py-1.5 text-sm">Cancel</button><button type="submit" className="bg-navy text-arch-white px-4 py-1.5 text-sm" data-testid="dist-save">Save</button></div>
            </form>
        </Modal>
    );
}

/* ============================================================
   Shared Modal
   ============================================================ */
function Modal({ children, onClose, title, testid }) {
    return (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" onClick={onClose} data-testid={testid}>
            <div className="bg-background border border-border w-full max-w-2xl my-6" onClick={(e) => e.stopPropagation()}>
                <div className="px-5 py-3 border-b border-border flex items-center justify-between">
                    <div className="font-display text-xl italic">{title}</div>
                    <button onClick={onClose}><X size={16} /></button>
                </div>
                {children}
            </div>
        </div>
    );
}
